import { useTranslation } from "react-i18next";
import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import styles from "./SecureInput.module.css";

const SecureInput = forwardRef((props, ref) => {
  const {
    id,
    type = "text",
    placeHolder,
    value: initialValue = "",
    onChange,
    onEnterPress,
    onClick,
    regExp,
    maxLength,
    readOnly,
    disabled,
    number,
    phone,
    label,
    autoFocus,
    autoComplete,
    inputmode,
    style: customStyle
  } = props;

  const input = useRef(null);
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const [isValid, setValid] = useState(regExp ? false : true);
  const [isEmpty, setEmpty] = useState(!initialValue);
  const isIOS = () => {
    return typeof window !== "undefined" && /iP(ad|hone|od)/.test(navigator.userAgent);
  };

  const isAndroid = () => {
    return typeof window !== "undefined" && /Android/i.test(navigator.userAgent);
  };

  // 값이 변경될 때 호출되는 핸들러
  const handleChange = (e) => {
    let inputValue = e.target.value;

    // 숫자만 입력 처리
    if (number || phone) {
      inputValue = inputValue.replace(/\D/g, "");
    }

    // 최대 길이 제한 (하이픈 추가 전에 처리)
    if (maxLength && phone) {
      // 전화번호의 경우 하이픈 없이 최대 길이 계산
      const numericMaxLength = maxLength - 2; // 하이픈 2개 제외
      if (inputValue.length > numericMaxLength) {
        inputValue = inputValue.slice(0, numericMaxLength);
      }
    } else if (maxLength) {
      // 일반 입력의 경우
      if (inputValue.length > maxLength) {
        inputValue = inputValue.slice(0, maxLength);
      }
    }

    // 휴대폰 번호 자동 하이픈 추가
    if (phone) {
      if (inputValue.length > 3 && inputValue.length <= 7) {
        // 010-1234 형식
        inputValue = inputValue.replace(/(\d{3})(\d+)/, "$1-$2");
      } else if (inputValue.length > 7) {
        // 010-1234-5678 형식
        inputValue = inputValue.replace(/(\d{3})(\d{4})(\d+)/, "$1-$2-$3");
      }
    }

    setValue(inputValue);

    // 상위 컴포넌트에 변경 사항 전달
    if (onChange) {
      // 전화번호의 경우 하이픈을 제거하고 숫자만 전달
      if (phone) {
        onChange(inputValue.replace(/-/g, ""));
      } else {
        onChange(inputValue);
      }
    }
  };

  // 외부에서 값이 변경될 때 내부 상태 업데이트
  useEffect(() => {
    if (initialValue !== undefined && initialValue !== value) {
      setValue(initialValue);
    }
  }, [initialValue]);

  // 유효성 검사 및 빈 값 확인
  useEffect(() => {
    // 유효성 검사
    if (regExp) {
      let validationResult = true;

      regExp.forEach((re) => {
        const eachValidationResult = re.exp.test(value);
        if (!eachValidationResult) {
          validationResult = false;
        }
      });

      setValid(validationResult);
    } else {
      setValid(true);
    }

    // 비어있는지 확인
    setEmpty(value.length === 0);
  }, [value, regExp]);

  // 포커스 이벤트 핸들러
  const handleFocus = () => {
    setIsFocused(true);

    // iOS 대응: 키보드 뜰 때 입력창이 가려지지 않도록 scroll 조정
    if (isIOS() && input.current) {
      const el = input.current;

      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const visualHeight = window.visualViewport?.height || window.innerHeight;
        const inputHeight = rect.height || 56;

        const scrollOffset = rect.top - (visualHeight / 2) + (inputHeight / 2);

        // ✅ 강제로 scrollTop 복구 후 중앙 정렬
        const savedY = window.scrollY;
        window.scrollTo(0, savedY); // iOS scrollIntoView 대응

        setTimeout(() => {
          window.scrollBy({
            top: scrollOffset,
            behavior: 'smooth'
          });
        }, 300);
      });
    }


    // Android 대응 (선택적)
    if (isAndroid() && input.current) {
      setTimeout(() => {
        input.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 250);
    }
  };

  // 포커스 아웃 이벤트 핸들러
  const handleBlur = () => {
    setIsFocused(false);

    if (isIOS()) {
      // scroll 복구 (선택적으로 적용)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 300);
    }
  };

  // 키 다운 이벤트 핸들러 (엔터 처리)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onEnterPress) {
      e.preventDefault();
      onEnterPress();
    }
  };

  // 숫자 입력 필드의 입력 스타일 계산
  const getInputStyle = () => {
    if (phone) {
      return {
        textAlign: 'left',
        fontVariantNumeric: 'tabular-nums'
      };
    } else if (id === 'residentNumberFront') {
      // 주민등록번호 앞자리 특화 스타일
      return {
        textAlign: 'left',
        fontVariantNumeric: 'tabular-nums',
        color: 'var(--main-color2)',
        fontWeight: '600'
      };
    } else if (id === 'residentNumberBack') {
      // 주민등록번호 뒷자리 특화 스타일
      return {
        textAlign: 'center',
        fontVariantNumeric: 'tabular-nums',
        color: 'var(--main-color2)',
        fontWeight: '600',
        fontSize: '20px',
        paddingLeft: '0',
        paddingRight: '0'
      };
    } else if (number) {
      // 일반 숫자의 길이에 따라 다른 스타일 적용
      return {
        textAlign: 'left',
        fontVariantNumeric: 'tabular-nums'
      };
    }
    return undefined;
  };

  // autoFocus 속성이 true일 때 컴포넌트 마운트 시 포커스
  useEffect(() => {
    if (autoFocus && input.current && document.activeElement !== input.current) {
      setTimeout(() => {
        input.current?.focus({ preventScroll: true });
      }, 300);
    }
  }, [autoFocus]);

  // ref를 통해 외부에서 접근 가능한 메서드 설정
  useImperativeHandle(ref, () => ({
    getId() {
      return id;
    },
    getValue() {
      return value;
    },
    setValue(newValue) {
      setValue(newValue);
    },
    isValid() {
      return isValid;
    },
    empty() {
      setValue("");
    },
    focus() {
      if (input.current) {
        input.current.focus();
      }
    }
  }));

  return (
    <div
      className={`${styles.inputContainer} ${isFocused ? styles.focused : ''} ${isValid ? '' : styles.invalid} ${disabled ? styles.disabled : ''} ${number || phone ? styles.numberInput : ''}`}
      style={customStyle}
      onClick={onClick}
    >
      {label && <label className={styles.label}>{label}</label>}

      <div className={styles.inputWrapper}>
        <input
          ref={input}
          id={id}
          type="text"
          inputMode={inputmode || (number || phone) ? "numeric" : undefined}
          pattern={(number || phone) ? "\d{6}" : undefined}
          className={styles.input}
          value={value}
          onChange={handleChange}
          onFocus={(e) => {
            handleFocus();
            if (props.onFocus) props.onFocus(e);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          style={getInputStyle()}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          onClick={onClick}
        />

        <div className={`${styles.placeholder} ${isFocused || !isEmpty ? styles.active : ''}`}>
          {placeHolder}
        </div>

        <div className={styles.underline}></div>
        <div className={`${styles.focusLine} ${isFocused ? styles.active : ''}`}></div>
      </div>

      {!isValid && !isEmpty && (
        <div className={styles.errorMessage}>
          {regExp?.find(re => !re.exp.test(value))?.message || t("유효하지 않은 입력입니다")}
        </div>
      )}
    </div>
  );
});

export default SecureInput; 